/**
 * Lanes inspector (docs/ux/project-tab-spec.md §1.8): a right pane on desktop (pushes ≥1440 px,
 * overlays below), a full-screen push view on mobile — never a bottom sheet. Opens from the
 * cached query data the tab already holds; live data (transcript, progress) subscribes after.
 * URL-addressable as `?i=<kind>:<id>[:tab]`.
 */

import { Link } from '@tanstack/react-router'
import {
  LANE_STATE_LABEL,
  LiveActivity,
  RUN_PHASE_LABEL,
  Transcript,
  useRunState,
} from '@viko/transcript-ui'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import type { ActionEntity } from '../../lib/actions.js'
import { useBackend } from '../../lib/backend.js'
import type { GoalController } from '../../lib/goals.js'
import { homeHref, projectHref, runHref, workstreamHref } from '../../lib/ia.js' // ia-shell
import { stackView } from '../../lib/landing-modes.js'
import type { AskRow, LaneCard, LanesInput, LanesModel } from '../../lib/lanes-model.js'
import { isLiveRun } from '../../lib/lanes-model.js'
import { useProgressSync } from '../../lib/progress-snapshot.js'
import { useRunMaterial, useRunWindow, viewAsks } from '../../lib/session.js'
import type { RunSummary, TaskDetail } from '../../lib/types.js'
import { ArtifactGallery } from '../Artifacts.js'
import { ConflictWait, GateEvidence, StackBlock, TargetLine } from '../LandingModes.js'
import { Glyph, LANE_GLYPH } from '../lanes/glyphs.js'
import { GoalInspector } from '../lanes/goal-strip.js'
import { evidenceText } from '../lanes/LiveRuns.js'
import { releaseInspector } from '../lanes/ReleaseStrip.js'
import type { LanesSlots } from '../lanes/slots.js'
import { SteerBar } from '../now/SteerBar.js'
import { Ago, errorText, useToast } from '../ui.js'
import { InspectorFrame } from './InspectorFrame.js'

export const INSPECT_MARK = 'lanes:inspector-open'

export interface InspectorProps {
  inspectKey: string
  model: LanesModel
  input: LanesInput
  slots: LanesSlots
  onInspect: (key: string) => void
  onClose: () => void
  goal?: GoalController
  /** Put starter text in the tab's composer (the goal inspector's "Ask Viko for milestones"). */
  onStarter?: (text: string) => void
}

type TaskTab = 'progress' | 'transcript' | 'evidence' | 'origin'
const TASK_TABS: [TaskTab, string][] = [
  ['progress', 'Progress'],
  ['transcript', 'Transcript'],
  ['evidence', 'Evidence'],
  ['origin', 'Origin'],
]

export function Inspector({
  inspectKey,
  model,
  input,
  slots,
  onInspect,
  onClose,
  goal,
  onStarter,
}: InspectorProps) {
  const [kind = '', id = '', tab] = inspectKey.split(':')
  const heading = useRef<HTMLHeadingElement>(null)
  const invoker = useRef<HTMLElement | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-focus and re-measure per inspected item
  useEffect(() => {
    invoker.current ??= document.activeElement as HTMLElement | null
    heading.current?.focus()
    try {
      performance.mark(`${INSPECT_MARK}:painted`)
      performance.measure(INSPECT_MARK, INSPECT_MARK, `${INSPECT_MARK}:painted`)
    } catch {
      // no start mark (deep link): nothing to measure
    }
  }, [inspectKey])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) close()
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
  })
  const close = () => {
    onClose()
    const back = invoker.current
    requestAnimationFrame(() => back?.isConnected && back.focus())
  }
  // core-loop: steer the inspected run (or the task's run) in place: `r` or the footer's Steer.
  const b = useBackend()
  const toast = useToast()
  const [steer, setSteer] = useState(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes per inspected item
  useEffect(() => setSteer(false), [inspectKey])
  const body = resolve(kind, id, tab, {
    model,
    input,
    slots,
    onInspect,
    ...(goal ? { goal } : {}),
    ...(onStarter ? { onStarter } : {}),
  })
  const e = body.entity
  const steerRunId = e?.kind === 'run' ? e.id : e?.kind === 'task' ? e.runId : undefined
  const steerRun = steerRunId ? input.runs.find((r) => r.runId === steerRunId) : undefined
  const handlers = useMemo(
    () => (steerRun?.machineId ? { reply: () => setSteer(true) } : undefined),
    [steerRun?.machineId],
  )
  return (
    <InspectorFrame
      skin="lk"
      kind={kind}
      title={body.title}
      {...(body.sub ? { sub: body.sub } : {})}
      onClose={close}
      headingRef={heading}
      entity={body.entity}
      projectId={model.projectId}
      {...(handlers ? { handlers } : {})}
      dialog
    >
      {steer && steerRun?.machineId ? (
        <SteerBar
          title={steerRun.title ?? steerRun.taskTitle ?? steerRun.runId}
          agent={steerRun.agent === 'codex' ? 'Codex' : 'Claude'}
          busy={isLiveRun(steerRun)}
          onSend={async (text, mode) => {
            const machineId = steerRun.machineId as string
            try {
              await b.dispatch('run.message', machineId, {
                runId: steerRun.runId,
                text,
                queue: mode === 'later' && isLiveRun(steerRun),
                clientMessageId: crypto.randomUUID(),
              })
              toast(mode === 'later' ? 'Sent after this step' : 'Sent')
              return true
            } catch (err) {
              toast(errorText(err), 'error')
              return false
            }
          }}
          onStop={() => {
            setSteer(false)
            void b
              .dispatch('run.interrupt', steerRun.machineId as string, {
                runId: steerRun.runId,
                cancelQueued: true,
              })
              .then(
                () => toast('Stopped'),
                (err) => toast(errorText(err), 'error'),
              )
          }}
          onClose={() => {
            setSteer(false)
            heading.current?.focus()
          }}
        />
      ) : null}
      {body.content}
    </InspectorFrame>
  )
}

interface Ctx {
  model: LanesModel
  input: LanesInput
  slots: LanesSlots
  onInspect: (key: string) => void
  goal?: GoalController
  onStarter?: (text: string) => void
}

function resolve(
  kind: string,
  id: string,
  tab: string | undefined,
  ctx: Ctx,
): { title: ReactNode; sub?: ReactNode; content: ReactNode; entity?: ActionEntity } {
  const { model, input } = ctx
  if (kind === 'task') {
    const card = model.cards.find((c) => c.id === id) ?? earlierCard(model, id)
    const task = input.tasks.find((t) => t.id === id)
    if (!card || !task) return missing('This task is no longer in this project.')
    return {
      entity: {
        kind: 'task',
        id,
        ...(card.runId ? { runId: card.runId } : {}),
        title: task.title,
      },
      title: task.title,
      sub: `${card.laneName}. ${card.subLabel ?? LANE_STATE_LABEL[card.state]}.`,
      content: (
        <TaskPanel
          key={id}
          card={card}
          task={task}
          initial={(TASK_TABS.find(([t]) => t === tab)?.[0] ?? 'progress') as TaskTab}
          ctx={ctx}
        />
      ),
    }
  }
  if (kind === 'run') {
    const run = input.runs.find((r) => r.runId === id)
    if (!run) return missing('This run is no longer listed.')
    return {
      entity: {
        kind: 'run',
        id: run.runId,
        live: isLiveRun(run),
        ...(run.branch ? { branch: run.branch } : {}),
        title: run.title ?? run.taskTitle ?? run.runId,
      },
      title: run.title ?? run.taskTitle ?? run.runId,
      sub: 'A session outside any task.',
      content: <RunPanel run={run} initial={tab === 'transcript' ? 'transcript' : 'progress'} />,
    }
  }
  if (kind === 'ask') {
    // now-inbox: hub item ids carry colons; keys encode them.
    const askId = decodeURIComponent(id)
    const ask = model.asks.find((a) => a.id === askId)
    // ---- now-inbox: the real decision view (thread, options, evidence, answer bar) ----
    const D = ctx.slots.DecisionInspector
    if (D)
      return {
        title: ask?.title ?? 'Decision',
        ...(ask?.agentLabel ? { sub: ask.agentLabel } : {}),
        content: (
          <D
            inspectKey={`ask:${askId}`}
            projectId={model.projectId}
            {...(ask ? { item: ask.item } : {})}
            onInspect={ctx.onInspect}
            fallback={
              ask ? (
                <AskPanel ask={ask} ctx={ctx} />
              ) : (
                missing('This decision was answered or withdrawn.').content
              )
            }
          />
        ),
      }
    // ---- end now-inbox ----
    if (!ask) return missing('This decision was answered or withdrawn.')
    return { title: ask.title, sub: ask.agentLabel, content: <AskPanel ask={ask} ctx={ctx} /> }
  }
  // ---- now-inbox: sealed decisions and "decide all" sessions ----
  if ((kind === 'decision' || kind === 'batch') && ctx.slots.DecisionInspector) {
    const D = ctx.slots.DecisionInspector
    return {
      title: kind === 'batch' ? 'Decide all' : 'Decision',
      content: (
        <D
          inspectKey={`${kind}:${id}`}
          projectId={model.projectId}
          onInspect={ctx.onInspect}
          fallback={missing('This decision was answered or withdrawn.').content}
        />
      ),
    }
  }
  // ---- end now-inbox ----
  if (kind === 'ws') {
    const lane = model.lanes.find((l) => l.id === id)
    const w = input.workstreams.find((x) => x.id === id)
    if (!lane || !w) return missing('This workstream is gone.')
    const cards = [...Object.values(lane.columns).flat(), ...lane.earlier]
    return {
      title: w.name,
      sub: `${cards.length} tasks${w.paused ? ' · paused' : ''}`,
      content: (
        <>
          {w.brief ? <p className="lk-muted whitespace-pre-wrap">{w.brief}</p> : null}
          <Facts
            rows={[
              ['Branch', w.branch ? <code key="b">{w.branch}</code> : 'None yet'],
              [
                'Agents now',
                lane.agents.map((a) => a.label).join(', ') || (lane.idleReason ?? '—'),
              ],
              ['Open', String(w.counts.open)],
              ['Proposed', String(w.counts.proposed)],
              ['Done', String(w.counts.done)],
            ]}
          />
          <CardList cards={cards} onInspect={ctx.onInspect} />
          <Link to={workstreamHref(w.id, input.project.id)} className="lk-link">
            Pop out: the full workstream
          </Link>
        </>
      ),
    }
  }
  if (kind === 'release') {
    return (
      releaseInspector(id, model, input, ctx.onInspect) ??
      missing('This lane is no longer in the project config.')
    )
  }
  if (kind === 'build') {
    const b = input.builds.find((x) => x.buildId === id)
    if (!b) return missing('This build is no longer listed.')
    return {
      entity: {
        kind: 'build',
        id: b.buildId,
        failed: String(b.status) === 'failed',
        title: b.title,
      },
      title: b.title,
      sub: [b.version, b.subtitle].filter(Boolean).join(' · '),
      content: (
        <>
          <Facts
            rows={[
              ['Status', b.status],
              ['Updated', <Ago key="u" ts={b.updatedAt ?? b.createdAt ?? undefined} />],
              ['Production', b.production ? 'Yes — releases always ask for a passkey' : 'No'],
              ['Contents', b.tasks.map((t) => t.title).join(', ') || 'Latest landed changes'],
              ...(b.failure?.cause
                ? ([['Failure', b.failure.cause]] as [string, ReactNode][])
                : []),
              ...(b.failure?.fix ? ([['Fix', b.failure.fix]] as [string, ReactNode][]) : []),
              ...(b.live
                ? ([
                    [
                      'Live',
                      `${b.live.version} (${b.live.commit.slice(0, 7)}) on ${b.live.environment}`,
                    ],
                  ] as [string, ReactNode][])
                : []),
            ]}
          />
          {(b.handoffs ?? []).map((h) => (
            <p key={h.url} className="grid gap-1">
              <a className="lk-link" href={h.url}>
                {h.label}
              </a>
              {h.note ? <span className="lk-muted">{h.note}</span> : null}
            </p>
          ))}
          {b.links.map((l) => (
            <a key={l.url} className="lk-link" href={l.url} target="_blank" rel="noreferrer">
              {l.label ?? l.url}
            </a>
          ))}
          <Link to={projectHref(input.project.id, 'releases')} className="lk-link">
            Open Builds
          </Link>
        </>
      ),
    }
  }
  if (kind === 'landing') {
    const l = input.landings.find((x) => x.id === id)
    if (!l) return missing('This landing is no longer listed.')
    return {
      entity: {
        kind: 'landing',
        id: l.id,
        title: `Landing ${l.branch ?? ''}`.trim(),
        landed: l.state === 'landed',
        ...(l.branch ? { branch: l.branch } : {}),
        ...(l.machineId ? { machineId: l.machineId } : {}),
        ...(l.workstreamId ? { workstreamId: l.workstreamId } : {}),
        ...(l.projectId ? { projectId: l.projectId } : {}),
        ...(l.runId ? { runId: l.runId } : {}),
      },
      title: `Landing ${l.branch ?? ''}`.trim(),
      sub: l.state.replace('_', ' '),
      content: (
        <>
          <Facts
            rows={[
              ['State', l.state.replace('_', ' ')],
              ['Step', l.step ?? '—'],
              ['Commit', l.commit ? <code key="c">{l.commit.slice(0, 8)}</code> : 'Not landed'],
              ['Why', l.reason ?? l.error ?? '—'],
              ['Updated', <Ago key="u" ts={l.updatedAt} />],
            ]}
          />
          <TargetLine landing={l} />
          <ConflictWait landing={l} />
          <GateEvidence landing={l} />
          {(() => {
            const st = stackView(input.landings, l)
            return st ? (
              <StackBlock stack={st} onOpen={(id) => ctx.onInspect(`landing:${id}`)} />
            ) : null
          })()}
          {l.recipe?.length ? (
            <ol className="lk-steps">
              {l.recipe.map((r) => (
                <li key={r.command}>
                  <Glyph
                    kind={
                      r.status === 'pass' || r.status === 'reused'
                        ? 'ok'
                        : r.status === 'fail'
                          ? 'fail'
                          : r.status === 'running'
                            ? 'run'
                            : 'idle'
                    }
                  />
                  <code className="text-[14px]">{r.command}</code>
                </li>
              ))}
            </ol>
          ) : null}
        </>
      ),
    }
  }
  if (kind === 'landed') {
    const today = model.cards.filter((c) => c.column === 'landed')
    const earlier = model.lanes.flatMap((l) => l.earlier)
    return {
      title: 'What landed',
      sub: `${today.length} today`,
      content: (
        <>
          <CardList cards={today} onInspect={ctx.onInspect} />
          {earlier.length ? (
            <>
              <h3 className="lk-h2">Earlier</h3>
              <CardList cards={earlier} onInspect={ctx.onInspect} />
            </>
          ) : null}
        </>
      ),
    }
  }
  if (kind === 'capacity') {
    const machines = input.project.machines
      .map((mid) => input.machines.find((m) => m.id === mid))
      .filter((m) => !!m)
    return {
      title: 'Capacity',
      sub: model.capacity?.text,
      content: (
        <>
          <Facts
            rows={machines.map((m) => [
              m.name,
              m.online
                ? `${m.caps ? `${m.caps.running} of ${m.caps.maxRuns} runs` : 'Online'}`
                : 'Offline',
            ])}
          />
          <h3 className="lk-h2">Ready to start</h3>
          <CardList
            cards={model.cards.filter((c) => c.state === 'queued' && !c.proposed)}
            onInspect={ctx.onInspect}
          />
        </>
      ),
    }
  }
  // ---- lanes-integrate: goal inspector (criteria, evidence, editor) ----
  if (kind === 'goal' && ctx.goal) {
    const titles = new Map<string, string>([
      ...input.tasks.map((t): [string, string] => [`task:${t.id}`, t.title]),
      ...input.landings.map((l): [string, string] => [`landing:${l.id}`, l.branch ?? l.id]),
      ...input.builds.map((b): [string, string] => [`build:${b.buildId}`, b.title]),
    ])
    return {
      title: 'Goal',
      content: (
        <GoalInspector
          goal={ctx.goal}
          sources={{ landings: input.landings, builds: input.builds, tasks: input.tasks, titles }}
          editing={tab === 'edit'}
          onInspect={ctx.onInspect}
          onStarter={ctx.onStarter}
        />
      ),
    }
  }
  // ---- end lanes-integrate ----
  if (kind === 'bark') {
    const B = ctx.slots.BarkInspector
    return {
      entity: { kind: 'bark', id, title: 'Your request' },
      title: 'Your request',
      content: B ? (
        <B projectId={model.projectId} barkId={id} onInspect={ctx.onInspect} />
      ) : (
        <p className="lk-muted">Bark details are not available in this build yet.</p>
      ),
    }
  }
  return missing('Nothing to show here.')
}

const missing = (text: string) => ({
  title: 'Not found',
  content: <p className="lk-muted">{text}</p>,
})

function earlierCard(model: LanesModel, id: string): LaneCard | undefined {
  return model.lanes.flatMap((l) => l.earlier).find((c) => c.id === id)
}

function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="lk-facts">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  )
}

function CardList({ cards, onInspect }: { cards: LaneCard[]; onInspect: (k: string) => void }) {
  if (!cards.length) return <p className="lk-muted">None.</p>
  return (
    <ul className="lk-steps">
      {cards.map((c) => (
        <li key={c.id}>
          <Glyph kind={LANE_GLYPH[c.state]} />
          <button
            type="button"
            className="lk-link !no-underline"
            onClick={() => onInspect(`task:${c.id}`)}
          >
            {c.title}
          </button>
          <span className="text-[14px] text-text-3">{c.subLabel ?? LANE_STATE_LABEL[c.state]}</span>
        </li>
      ))}
    </ul>
  )
}

function TabBar<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: [T, string][]
  value: T
  onChange: (t: T) => void
}) {
  return (
    <div className="lk-insp__tabs" role="tablist" aria-label="Inspector views">
      {tabs.map(([t, label]) => (
        <button
          key={t}
          type="button"
          role="tab"
          aria-selected={t === value}
          onClick={() => onChange(t)}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

// ---- task ---------------------------------------------------------------------------------------

function TaskPanel({
  card,
  task,
  initial,
  ctx,
}: {
  card: LaneCard
  task: TaskDetail
  initial: TaskTab
  ctx: Ctx
}) {
  const [tab, setTab] = useState<TaskTab>(initial)
  useEffect(() => setTab(initial), [initial])
  const run = card.runId ? ctx.input.runs.find((r) => r.runId === card.runId) : undefined
  const landed = ctx.input.landings.find((l) => l.state === 'landed' && l.taskIds.includes(task.id))
  return (
    <>
      <div className="lk-chips">
        <span className={`lk-chip is-strong`}>
          <Glyph kind={LANE_GLYPH[card.state]} />
          {card.subLabel ?? LANE_STATE_LABEL[card.state]}
        </span>
        {card.agent ? (
          <span className="lk-chip">
            <span className={`lk-dot is-${card.agent.agent}`} aria-hidden="true" />
            {card.agent.label}
          </span>
        ) : null}
        {card.chips
          .filter((c) => c.kind === 'land')
          .map((c) => (
            <span key={c.label} className={`lk-chip${c.tone ? ` is-${c.tone}` : ''}`}>
              {c.label}
            </span>
          ))}
      </div>
      {card.state === 'needs-you' && card.askId ? (
        <button
          type="button"
          className="lk-chip is-needs justify-self-start"
          onClick={() => ctx.onInspect(`ask:${card.askId}`)}
        >
          <Glyph kind="wait" />
          Answer to resume: {card.askTitle}
        </button>
      ) : null}
      <TabBar tabs={TASK_TABS} value={tab} onChange={setTab} />
      <div role="tabpanel" className="grid gap-3">
        {tab === 'progress' ? (
          run ? (
            <RunProgress run={run} />
          ) : (
            <Facts
              rows={[
                ['State', card.subLabel ?? LANE_STATE_LABEL[card.state]],
                ['Agent', 'Starts when capacity frees up'],
                ['Priority', String(task.priority)],
                ['Blocked by', task.blockedReason ?? '—'],
              ]}
            />
          )
        ) : null}
        {tab === 'transcript' ? (
          run ? (
            <RunTranscript run={run} />
          ) : (
            <p className="lk-muted">No run yet. It starts when a machine frees up.</p>
          )
        ) : null}
        {tab === 'evidence' ? (
          <>
            {run ? <RunEvidence run={run} /> : null}
            <Facts
              rows={[
                [
                  'Commit',
                  landed?.commit ? (
                    <span key="c">
                      <code>{landed.commit.slice(0, 8)}</code> verified on main
                    </span>
                  ) : (
                    'Not landed'
                  ),
                ],
                ['Checked by', landed ? 'Viko re-ran the recipe on the landing machine' : '—'],
              ]}
            />
            <ArtifactGallery taskId={task.id} />
          </>
        ) : null}
        {tab === 'origin' ? (
          <>
            <Facts
              rows={[
                ['Came from', originText(task)],
                ['Created', <Ago key="c" ts={task.createdAt} />],
                ['Updated', <Ago key="u" ts={task.updatedAt} />],
              ]}
            />
            {task.barkIds?.length ? (
              <div className="lk-chips">
                {task.barkIds.map((b) => (
                  <button
                    key={b}
                    type="button"
                    className="lk-chip"
                    onClick={() => ctx.onInspect(`bark:${b}`)}
                  >
                    Open the bark
                  </button>
                ))}
              </div>
            ) : null}
            {task.description ? (
              <p className="lk-muted whitespace-pre-wrap">{task.description}</p>
            ) : null}
            {task.acceptance.length ? (
              <ul className="lk-steps">
                {task.acceptance.map((a) => (
                  <li key={a}>
                    <Glyph kind="idle" />
                    {a}
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : null}
      </div>
    </>
  )
}

function originText(t: TaskDetail): string {
  if (t.barkIds?.length) return 'Your bark'
  if (t.provenance === 'agent') return 'Suggested by an agent'
  if (t.provenance === 'brain') return 'A capture, sorted by Viko'
  return 'You added it'
}

function RunPanel({ run, initial }: { run: RunSummary; initial: 'progress' | 'transcript' }) {
  const [tab, setTab] = useState(initial)
  return (
    <>
      <TabBar
        tabs={[
          ['progress', 'Progress'],
          ['transcript', 'Transcript'],
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === 'progress' ? <RunProgress run={run} /> : <RunTranscript run={run} />}
    </>
  )
}

function useRunView(run: RunSummary) {
  const b = useBackend()
  const { view, older } = useRunWindow(run.runId)
  const store = useMemo(
    () => b.transcriptStore(run.runId, b.artifactAccess.accountId),
    [b, run.runId],
  )
  const sync = useProgressSync(run.runId, view, store, run.headSeq)
  return { view, sync, older }
}

/** Progress/evidence tabs read the materialized state only (no event history). */
function RunProgress({ run }: { run: RunSummary }) {
  const view = useRunMaterial(run.runId)
  const rs = useRunState(view, { lifecycle: { ended: run.status === 'done' } })
  const asks = viewAsks(view)
  return (
    <>
      <Facts
        rows={[
          ['State', RUN_PHASE_LABEL[rs.phase]],
          ['Why', rs.reason],
          ['Last activity', <Ago key="a" ts={view.activity.lastEventAt ?? run.updatedAt} />],
        ]}
      />
      <LiveActivity
        activity={view.activity}
        asks={asks}
        todos={null}
        usage={null}
        offline={view.connection === 'offline'}
        stateLabel={rs.label}
      />
      <Link to={runHref(run.runId, run.projectId)} className="lk-link">
        Open full conversation
      </Link>
    </>
  )
}

function RunEvidence({ run }: { run: RunSummary }) {
  const view = useRunMaterial(run.runId)
  const rs = useRunState(view)
  const tests = evidenceText(
    rs.progress.latest,
    rs.phase === 'working' || rs.phase === 'making-progress',
  )
  return (
    <Facts
      rows={[
        ['Tests', tests ?? 'None run yet'],
        [
          'Checks',
          rs.progress.latest.map((c) => `${c.name ?? c.kind}: ${c.status}`).join(', ') || '—',
        ],
        ['Commits', String(view.progress.commits.length)],
      ]}
    />
  )
}

function RunTranscript({ run }: { run: RunSummary }) {
  const { view, sync, older } = useRunView(run)
  return (
    <>
      <div className="lk-embed">
        <Transcript
          view={view}
          sync={sync}
          composer={false}
          showLevelControl={false}
          {...(older ? { older } : {})}
        />
      </div>
      <Link to={runHref(run.runId, run.projectId)} className="lk-link">
        Open full conversation
      </Link>
    </>
  )
}

// ---- decision (read-only) -------------------------------------------------------------------------

function AskPanel({ ask, ctx }: { ask: AskRow; ctx: Ctx }) {
  const run = ctx.input.runs.find((r) => r.runId === ask.runId)
  const task = ask.taskId ? ctx.input.tasks.find((t) => t.id === ask.taskId) : undefined
  return (
    <>
      <p className="m-0 text-[16px] leading-6">
        {ask.locked ? 'Encrypted: unlock this browser to read' : ask.question}
      </p>
      <Facts
        rows={[
          ['Asked by', run ? (ask.agentLabel ?? run.agent ?? '—') : '—'],
          ['Lane', ask.workstreamName ?? '—'],
          [
            'Task',
            task ? (
              <button
                key="t"
                type="button"
                className="lk-link"
                onClick={() => ctx.onInspect(`task:${task.id}`)}
              >
                {task.title}
              </button>
            ) : (
              '—'
            ),
          ],
          ['Leverage', ask.leverage.join(' · ')],
          [
            'If you skip it',
            ask.blocking ? 'This run stays paused; other work continues.' : 'Nothing is blocked.',
          ],
        ]}
      />
      {ask.options.length ? (
        <section className="grid gap-2" aria-labelledby="lk-ask-opts">
          <h3 id="lk-ask-opts" className="lk-h2">
            Options
          </h3>
          <ul className="lk-steps !gap-3">
            {ask.options.map((o) => (
              <li key={o.label} className="!grid !gap-0.5">
                <span className="font-semibold">
                  {o.label}
                  {o.suggested ? (
                    <span className="ml-2 text-[14px] text-text-3">Suggested</span>
                  ) : null}
                </span>
                {o.description ? (
                  <span className="text-[14px] text-text-2">{o.description}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <div className="flex flex-wrap gap-3">
        <Link
          to={
            ask.runId
              ? runHref(ask.runId, ctx.input.project.id)
              : homeHref({ v: 'needs', project: ctx.input.project.id })
          }
          className="vk-btn vk-btn--primary vk-btn--sm"
        >
          <span>{ask.runId ? 'Answer in the conversation' : 'Answer in Needs you'}</span>
        </Link>
      </div>
      {run ? (
        <section className="grid gap-2" aria-labelledby="lk-ask-tx">
          <h3 id="lk-ask-tx" className="lk-h2">
            Conversation
          </h3>
          <RunTranscript run={run} />
          <h3 className="lk-h2">Artifacts from this run</h3>
          <ArtifactGallery runId={run.runId} />
        </section>
      ) : null}
    </>
  )
}
